/**
 * Badge bibliothèque : partages non vus et sondages en attente.
 */

import { useCallback, useEffect, useRef } from 'react';
import { BACKEND_URL } from '../config';
import type { LibraryNewShareEvent } from '../types/library-notifications';

interface UseLibraryNotificationsOptions {
  pseudo: string;
  compet: boolean;
  activeTab: string;
  onSummary: (summary: { newShareCount: number; pendingPollCount: number }) => void;
}

export function useLibraryNotifications({
  pseudo,
  compet,
  activeTab,
  onSummary,
}: UseLibraryNotificationsOptions): {
  refresh: () => Promise<void>;
  markSeen: () => Promise<void>;
} {
  const onSummaryRef = useRef(onSummary);
  onSummaryRef.current = onSummary;

  const refresh = useCallback(async () => {
    if (!compet) {
      onSummaryRef.current({ newShareCount: 0, pendingPollCount: 0 });
      return;
    }
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/library/notifications?pseudo=${encodeURIComponent(pseudo)}`,
      );
      if (res.ok) {
        const data = await res.json() as { newShareCount: number; pendingPollCount: number };
        onSummaryRef.current({
          newShareCount: data.newShareCount ?? 0,
          pendingPollCount: data.pendingPollCount ?? 0,
        });
      }
    } catch { /* ignore */ }
  }, [pseudo, compet]);

  const markSeen = useCallback(async () => {
    if (!compet) return;
    try {
      await fetch(
        `${BACKEND_URL}/api/library/mark-seen?pseudo=${encodeURIComponent(pseudo)}`,
        { method: 'POST' },
      );
      await refresh();
    } catch { /* ignore */ }
  }, [pseudo, compet, refresh]);

  useEffect(() => {
    void refresh();
    if (!compet) return;
    const id = setInterval(() => { void refresh(); }, 60_000);
    return () => clearInterval(id);
  }, [refresh, compet]);

  useEffect(() => {
    if (activeTab === 'library') {
      void refresh();
    }
  }, [activeTab, refresh]);

  useEffect(() => {
    const handler = () => { void refresh(); };
    window.addEventListener('library-new-share', handler);
    return () => window.removeEventListener('library-new-share', handler);
  }, [refresh]);

  return { refresh, markSeen };
}

export function dispatchLibraryNewShare(data: LibraryNewShareEvent): void {
  window.dispatchEvent(new CustomEvent('library-new-share', { detail: data }));
}
